import { Injectable } from '@angular/core';

import { Observable, BehaviorSubject, Subscription, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

import { TargetingSearch } from '../models/targeting-search.model';
import { TargetingListList } from '../models/targeting-list-list.model';
import { TargetingSearchesApiService } from '../../../api/targeting/targeting-searches.api.service';
import { TargetingResultModel } from '../models/targeting-result.model';
import { TargetingList } from '../models/targeting-list.model';
import { TargetingListsApiService } from '../../../api/targeting/targeting-lists.api.service';
import { DiscoverRoutingService } from '../../DiscoverRouter/services/discover-routing.service';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { TargetingSearchResponseApiModel } from '../../../api/targeting/model/targeting-search-response.api.model';
import { JournodexSubjectsService } from '../../../Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '../../Core/services/journodex-enums.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ContactModel } from '../../../Common/TargetingShared/model/contact.model';
import { ContactsPublicApi } from '../../../api/targeting/contacts-public.api';
import { DiscoverRouteTypeEnum } from '../../DiscoverRouter/enums/discover-route-type.enum';
import { TargetingQueryApi, TargetingSearchApi } from '../../../api/targeting/model/targeting-search.api.model';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { OutletModel } from '../models/outlet.model';
import { BaseJournodexEntityModel } from '../models/base-journodex-entity.model';
import { SearchModeEnum } from '../../../Common/TargetingShared/enum/search-mode.enum';
import { BaseSearchService, TargetingFacetCollection, FacetValueModel } from './base-search.service';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { StatusFilterService } from '../components/TargetingResults/StatusFilter/status-filter.service';
import { EmploymentModel } from '../models/employment.model';
import { TargetingRhpContextsService } from '../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { SortFieldEnum } from '../../../Common/TargetingShared/enum/SortField.enum';
import { TargetingResultFactory } from '../factories/targeting-result.factory';
import { JournodexEntityFactory } from '../factories/journodex-entity.factory';
import { circulationTypeToSortType } from '../models/circulation.model';
import { TargetingSearchFacetsEnum } from '../../../api/targeting/enum/targeting-search-facets.enum';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { UserAppTargetingDisplaySettingsModel } from '../../../Core/Users/models/user-app-default-display-preferences.model';
import { FilterSectionEnum } from '../../../Common/Filter/enums/filter-section.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DistributionApi } from '@api/distributions/distribution.api';
import { EntityType } from '@api/targeting/enum/entity.type.enum';
import { OUTLET_SORT_FIELDS } from '@Common/TargetingShared/model/outlet-sort-fields.const';

enum Mode {
    searches,
    targetingLists,
    other
}

@Injectable()
export class TargetingSearchService extends BaseSearchService {
    // TODO: Can probably factor these common routes out for multiple Targeting services
    private readonly targetingListRoutes = [
        DiscoverRouteTypeEnum.targetingListHype,
        DiscoverRouteTypeEnum.targetingListSearchResult,
        DiscoverRouteTypeEnum.privateContacts
    ];
    private readonly savedSearchesRoutes = [
        DiscoverRouteTypeEnum.targetingSearchHype,
        DiscoverRouteTypeEnum.targetingSearch,
        DiscoverRouteTypeEnum.targetingSearchUpdate,
        DiscoverRouteTypeEnum.targetingResults,
        DiscoverRouteTypeEnum.targetingSavedSearch,
        DiscoverRouteTypeEnum.targetingSavedSearchResults,
    ];

    readonly discoverRouteTypes = DiscoverRouteTypeEnum;

    private currentMode: Mode = null;

    private geoCodeResults:  string[] = null;
    private geoCodeResults$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(this.geoCodeResults);

    private focusedResult:  TargetingResultModel;
    private focusedResult$: BehaviorSubject<TargetingResultModel> = new BehaviorSubject<TargetingResultModel>(null);

    private rhpSelectedEmployment$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);

    // TODO: remove this
    private profileEntity:  ContactModel | OutletModel;
    private profileEntity$: BehaviorSubject<BaseJournodexEntityModel> = new BehaviorSubject<BaseJournodexEntityModel>(null);

    private setActiveSearchTypeComplete$ = new Subject<void>();

    public search: TargetingSearch;
    private list: TargetingList;

    private filtersLocked: boolean = false;
    private filtersLocked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.filtersLocked);
    private searchIncludesPrivateContacts: boolean = true;
    private searchIncludesPrivateContacts$: BehaviorSubject<boolean> =  new BehaviorSubject<boolean>(this.searchIncludesPrivateContacts);
    private readonly lockedFiltersSessionKey: string = 'targeting-filters';

    protected mapSearchRequest: Subscription;

    public mapView: boolean = false;
    public autoFocusFirstResult: boolean = true;
    private allowActiveNoticeFilter: boolean = false;

    private initApplyFilter$ = new Subject<void>();

    outletSortFields: LocalizedEnum[] = OUTLET_SORT_FIELDS;

    contactSortFields: LocalizedEnum[] = [
        {
            enumValue: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            displayString: 'Discover.targeting.sortFields.uniqueVisitors'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_CIRCULATION,
            displayString: 'Discover.targeting.sortFields.circulation'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_VIEWERS,
            displayString: 'Discover.targeting.sortFields.viewers'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_LISTENERS,
            displayString: 'Discover.targeting.sortFields.listeners'
        },
        {
            enumValue: SortFieldEnum.CIRCULATION_FOLLOWERS,
            displayString: 'Discover.targeting.sortFields.followers'
        },
        {
            enumValue: SortFieldEnum.FIRST_NAME,
            displayString: 'Discover.targeting.sortFields.firstName'
        },
        {
            enumValue: SortFieldEnum.LAST_NAME,
            displayString: 'Discover.targeting.sortFields.lastName'
        },
        {
            enumValue: SortFieldEnum.JOB_TITLE,
            displayString: 'Discover.targeting.sortFields.jobTitle'
        },
        {
            enumValue: SortFieldEnum.OUTLET_NAME,
            displayString: 'Discover.targeting.sortFields.outletName'
        },
        {
            enumValue: SortFieldEnum.COUNTRY_NAME,
            displayString: 'Common.country'
        },
        {
            enumValue: SortFieldEnum.PROVINCE_NAME,
            displayString: 'Common.province'
        },
        {
            enumValue: SortFieldEnum.CITY_NAME,
            displayString: 'Common.city'
        }
    ];

    constructor(
        protected discoverRoutingService: DiscoverRoutingService,
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected contactsPublicApi: ContactsPublicApi,
        protected outletsPublicApi: OutletsPublicApi,
        protected targetingRhpContextsService: TargetingRhpContextsService,
        protected statusFilterService: StatusFilterService,
        private journodexEntityFactory: JournodexEntityFactory,
        protected targetingResultFactory: TargetingResultFactory,
        private userPreferencesService: UserPreferencesService,
        protected currentUserStateService: CurrentUserStateService,
        protected distributionApiService: DistributionApi
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApiService
        );
        this.setFilters(this.allFilters);

        this.subscriptions.push(
            this.discoverRoutingService.getCurrentRoute().subscribe(route => {
                // prevents results from a previous search/list from showing
                // up temporarily before performSearch() kicks off
                this.clearSearch();
                this.setPrimaryFacets(null);
                this.setFilteredFacets(null);
                this.pageCriteria.page = 1;
                this.setMode(route.type);
                this.allowActiveNoticeFilter = this.shouldAllowActiveNoticeFilter(this.currentMode, route.type);
                this.loadLockedFiltersFromSession();
                if (route.type === DiscoverRouteTypeEnum.influencerStreams) {
                    // set to false so that focused entity won't be set to the first result of all contacts search in influencer streams
                    // ideally, we want to set focused entity in outlet contacts tab through influencerStreamsService.setFocusedMention()
                    this.autoFocusFirstResult = false;
                } else {
                    this.autoFocusFirstResult = true;
                }
            }),

            this.discoverRoutingService.getCurrentRoute().pipe(
                map(() => this.currentMode),
                distinctUntilChanged(),
                // We have to wait until after setActiveSearch/List to set display settings
                switchMap(() => this.setActiveSearchTypeComplete$.pipe(
                    take(1),
                )),
                switchMap(() => this.userPreferencesService.getUserAppPreferences())
            ).subscribe(userAppPrefs => {
                if (this.currentMode === Mode.targetingLists) {
                    this.setDisplaySettings(userAppPrefs.targetingPrefs.targetedListsPrefs.defaultDisplaySettings);
                } else if (this.currentMode === Mode.searches) {
                    this.setDisplaySettings(userAppPrefs.targetingPrefs.savedSearchesPrefs.defaultDisplaySettings);
                }
            }),
        );
    }

    applyInitFiltersOnListReady(filter: TargetingSearch) {
        this.filtersLoading$.next(true);

        this.initApplyFilter$.pipe(take(1)).subscribe({
            next: () =>  {
                this.populateFiltersFromSearch(filter);
                this.applyFilters(true);
                this.filtersLoading$.next(false);
            }
        });

        // Execute immediately when no list filter is present, otherwise wait
        if (filter.list.isEmpty()) {
            this.initApplyFilter$.next();
        }
    }

    shouldAllowActiveNoticeFilter(mode: Mode, routeType: DiscoverRouteTypeEnum) {
        if (mode !== Mode.targetingLists) {
            return false;
        }
        if (routeType === this.discoverRouteTypes.privateContacts) {
            return false;
        }
        return true;
    }

    setMode(routeType: DiscoverRouteTypeEnum) {
        if (this.targetingListRoutes.includes(routeType)) {
            this.currentMode = Mode.targetingLists;
        } else if (this.savedSearchesRoutes.includes(routeType)) {
            this.currentMode = Mode.searches;
        } else {
            this.currentMode = Mode.other;
        }
    }

    getFilterDefinitions(): FilterTypeModel[] {
        let filters: FilterTypeModel[] = [
            // Contact/Outlet Details section
            this.contactNameFilter,
            this.outletNameFilter,
            this.jobRoleFilter,
            this.jobTitleFilter,
            this.locationFilter,

            // Coverage Profile section
            this.subjectFilter,
            this.languageFilter,
            this.targetAudienceRegionFilter,
            this.coverageFocusFilter,
            this.audienceReachFilter,
            this.marketSectorFilter,
            this.mediaTypeFilter,
            this.productTypeFilter,
            this.productFrequencyFilter,
            this.targetAudienceFilter,

            // Organization section
            this.listFilter,
            this.distributionFilter,
        ];

        // TODO : rather than excluding it, we probably need to have it
        // but with outlet labels.
        if ((this.search && this.search.searchMode !== SearchModeEnum.outlet)
            || this.list) {

            this.contactNameFilter.section = FilterSectionEnum.CONTACT_DETAIL;
            this.jobRoleFilter.section = FilterSectionEnum.CONTACT_DETAIL;
            this.jobTitleFilter.section = FilterSectionEnum.CONTACT_DETAIL;
            this.locationFilter.section = FilterSectionEnum.CONTACT_DETAIL;
            this.outletNameFilter.section = FilterSectionEnum.CONTACT_DETAIL;
            const statusFilter = this.statusFilter;
            if (this.search?.searchMode === SearchModeEnum.contact) {
                statusFilter.entityType = EntityType.CONTACT;
            }
            filters.push(statusFilter);
        } else if (this.search && this.search.searchMode === SearchModeEnum.outlet) {
            this.contactNameFilter.section = FilterSectionEnum.OUTLET_DETAIL;
            this.outletNameFilter.section = FilterSectionEnum.OUTLET_DETAIL;
            this.locationFilter.section = FilterSectionEnum.OUTLET_DETAIL;
            filters = filters.filter(x => (x !== this.jobTitleFilter) && (x !== this.jobRoleFilter));
            const statusFilter = this.statusFilter;
            statusFilter.entityType = EntityType.OUTLET;
            filters.push(statusFilter);
        }
        if (this.list) {
            this.contactNameFilter.section = FilterSectionEnum.CONTACT_OUTLET_DETAIL;
            this.jobRoleFilter.section = FilterSectionEnum.CONTACT_OUTLET_DETAIL;
            this.jobTitleFilter.section = FilterSectionEnum.CONTACT_OUTLET_DETAIL;
            this.locationFilter.section = FilterSectionEnum.CONTACT_OUTLET_DETAIL;
            this.outletNameFilter.section = FilterSectionEnum.CONTACT_OUTLET_DETAIL;
        }

        // TODO: add aoc and list filter, alphabetically. Backend not implemented at the moment
        return filters;
    }

    getGeoCodeResults(): Observable<string[]> {
        return this.geoCodeResults$;
    }

    setActiveSearch(search: TargetingSearch) {
        this.list = null;
        this.search = search;
        if (search) {
            switch (search.searchMode) {
                case SearchModeEnum.outlet:
                    this.targetingSortFields$.next(this.outletSortFields);
                    if (search.audienceReachFilter?.type) {
                        this.setSortFieldWithDefault(this.outletSortFields, circulationTypeToSortType.get(search.audienceReachFilter.type));
                    } else {
                        this.setSortFieldWithDefault(this.contactSortFields);
                    }
                    break;
                case SearchModeEnum.contact:
                default:
                    this.targetingSortFields$.next(this.contactSortFields);

                    this.statusFilterService.setContactTypeFromSearch(search.includePrivateContacts);
                    if (search.audienceReachFilter?.type) {
                        this.setSortFieldWithDefault(this.outletSortFields, circulationTypeToSortType.get(search.audienceReachFilter.type));
                    } else if (search.privateContactsOnly) {
                        this.setSortFieldWithDefault(this.contactSortFields, SortFieldEnum.LAST_NAME);
                    } else {
                        this.setSortFieldWithDefault(this.contactSortFields);
                    }
                    break;
            }
        }
        this.setFilters(this.getFilterDefinitions());
        this.setActiveSearchTypeComplete$.next();
    }

    setActiveList(list: TargetingList) {
        this.search = null;
        this.list = list;

        this.targetingSortFields$.next(this.contactSortFields);
        this.setSortFieldWithDefault(this.contactSortFields);
        this.setFilters(this.getFilterDefinitions());
        this.setActiveSearchTypeComplete$.next();
    }

    async setSearchResults(response: TargetingSearchResponseApiModel): Promise<void> {
        await super.setSearchResults(response);

        // Results are emitted by fetchListMemberships when it completes
        // so the blue checkmarks are present
        if (this.autoFocusFirstResult) {
            if (this.results.length > 0) {
                this.setFocusedResult(this.results[0]);
            } else {
                this.setFocusedResult(null);
            }
        }
    }

    performSearch(search: TargetingSearch) {
        if (!this.filtersAreLocked()) {
            this.clearFilters();
        }
        this.setActiveSearch(search);

        if (this.mapView) {
            this.rectangleQuery = {
                lowerLeftCorner: '-90,-180',
                upperRightCorner: '90,180'
            };
        }

        this.doSearch(true, true);
    }

    performListSearch(list: TargetingList) {
        if (!this.filtersAreLocked()) {
            this.clearFilters();
        }
        this.setActiveList(list);

        if (this.mapView) {
            this.rectangleQuery = {
                lowerLeftCorner: '-90,-180',
                upperRightCorner: '90,180'
            };
        }

        this.doSearch(true, true);
    }


    public doSearch(
        refreshPrimaryFacets: boolean = false,
        refreshFilteredFacets: boolean = false,
        updateMap: boolean = true): Observable<TargetingSearchResponseApiModel> {
        if (!this.search && !this.list) {
            this.clearSearch();

            return;
        }

        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        if (this.mapSearchRequest) {
            this.mapSearchRequest.unsubscribe();
            this.mapSearchRequest = null;
        }

        if (this.search instanceof TargetingSearch) {
            this.search.rectangleQuery = this.rectangleQuery;
        }

        const search = cloneDeep(this.search || this.list);


        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        if (this.mapView && updateMap) {
            const mapSearchRequest: Observable<TargetingSearchResponseApiModel> = this.mapSearch(search, filters);
            this.mapSearchRequest = mapSearchRequest.subscribe();
        }

        const mainSearchRequest: Observable<TargetingSearchResponseApiModel> = this.fastSearch(search, filters);

        // just to make sure it runs, in case the caller doesn't subscribe
        this.searchLoading$.next(true);
        this.mainSearchRequest = mainSearchRequest.subscribe({
            next: () => {
                this.mainSearchRequest = null;
            },
            error: (err) => {
                console.log(err);
            }
        });

        let filteredFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshFilteredFacets) {
            if (this.filteredFacetRequest) {
                this.filteredFacetRequest.unsubscribe();
            }
            filteredFacetRequest = this.facetSearch(search, filters);

            this.filteredFacetRequest = filteredFacetRequest.subscribe(collection => {
                this.setFilteredFacets(collection);
                this.filteredFacetRequest = null;
            });
        }

        let primaryFacetRequest: Observable<TargetingFacetCollection>;
        if (refreshPrimaryFacets) {
            if (this.primaryFacetRequest) {
                this.primaryFacetRequest.unsubscribe();
            }
            if (isFiltered || !filteredFacetRequest) {
                primaryFacetRequest = this.facetSearch(search);
            } else {
                primaryFacetRequest = filteredFacetRequest;
            }
            this.primaryFacetRequest = primaryFacetRequest.subscribe(collection => {
                collection.facets.forEach(
                    (facet, key) => {
                        this.setZeroFacetValues(facet, key);
                    }
                );
                this.setPrimaryFacets(collection);
                this.primaryFacetRequest = null;
            });
        }

        return mainSearchRequest;
    }

    public setZeroFacetValues(facets: FacetValueModel[], key: TargetingSearchFacetsEnum) {
        // Observable that returns an array of filter values
        let filterValuesObs: Observable<any[]>;
        // Getting the filter values
        switch (key) {
            case TargetingSearchFacetsEnum.subject:
                filterValuesObs = of(this.subjectFilter.getValue());
                break;
            case TargetingSearchFacetsEnum.mediaType:
                filterValuesObs = of(this.mediaTypeFilter.getValue());
                break;
            case TargetingSearchFacetsEnum.productType:
                filterValuesObs = of(this.productTypeFilter.getValue());
                break;
            case TargetingSearchFacetsEnum.marketSector:
                filterValuesObs = of(this.marketSectorFilter.getValue());
                break;
            case TargetingSearchFacetsEnum.coverageFocus:
                filterValuesObs = of(this.coverageFocusFilter.getValue());
                break;
            case TargetingSearchFacetsEnum.productFrequency:
                filterValuesObs = of(this.productFrequencyFilter.getValue());
                break;
            case TargetingSearchFacetsEnum.targetAudience:
                filterValuesObs = of(this.targetAudienceFilter.getValue());
                break;
            case TargetingSearchFacetsEnum.language:
                filterValuesObs = of(this.languageFilter.getValue());
                break;
            case TargetingSearchFacetsEnum.country:
                filterValuesObs = this.journodexRegionService
                    .getByGeographyIds(this.countryFilter.getValue()).pipe(
                        map(countries => countries.map(country => country.id))
                    );
                break;
            case TargetingSearchFacetsEnum.province:
                filterValuesObs =  this.journodexRegionService
                    .getByGeographyIds(this.provinceFilter.getValue()).pipe(
                        map(provinces => provinces.map(province => province.id))
                    );
                break;
            case TargetingSearchFacetsEnum.city:
                filterValuesObs =  this.journodexRegionService
                    .getByGeographyIds(this.cityFilter.getValue()).pipe(
                        map(cities => cities.map(city => city.id))
                    );
                break;
            case TargetingSearchFacetsEnum.targetAudienceRegion:
                const allLocations = [...this.aocCityFilter.getValue(), ...this.aocProvinceFilter.getValue(), ...this.aocCountryFilter.getValue()];
                filterValuesObs = of(allLocations);
                break;
            case TargetingSearchFacetsEnum.jobRole:
                filterValuesObs = of(this.jobRoleFilter.getValue());
                break;
        }
        // getting the values
        filterValuesObs?.subscribe(filterValues => {
            // iterating through the filter values and adding them to facet if they don't exists (Used for saved filters)
            filterValues.forEach(filterValue => {
                filterValue = filterValue.toString();
                if (!facets.find(facet => facet.value === filterValue)) {
                    facets.push(
                        {
                            value: filterValue,
                            count: 0,
                        }
                    );
                }
            });
            // Setting default value of zero for any facet that doesn't have a count
            facets.forEach(facet => {
                if (!(facet?.count >= 0)){
                    facet.count = 0;
                }
            });
        });
    }

    mapSearch(search: TargetingSearch | TargetingList, filters?: TargetingSearch): Observable<TargetingSearchResponseApiModel> {

        let searchRequest: Observable<TargetingSearchResponseApiModel>;
        this.geoCodeResults$.next(null);

        if (search instanceof TargetingSearch) {
            searchRequest = this.targetingSearchesApiService.performMapSearch(
                search.toApiModel(false, filters).query
            );
        } else if (search instanceof TargetingList) {
            // TODO: Pull out common list api conversions
            let query: TargetingQueryApi = {};
            if (filters) {
                query = filters.toApiModel(false, filters).query;
            }
            query.searchMode = null;
            query.rectangleQuery = this.rectangleQuery;
            query.includeGeoRestrictions = false;

            if (query.list) {
                query.list.include = [search.id];
            } else {
                query.list = {
                    includeAll: [],
                    include: [search.id],
                    exclude: []
                };
            }

            searchRequest = this.targetingSearchesApiService.performMapSearch(query);
        }

        return searchRequest.pipe(map(response => {
            this.geoCodeResults = response.geoCodeResults;
            this.geoCodeResults$.next(this.geoCodeResults);

            return response;
        }));
    }

    getFocusedResult() {
        return this.focusedResult$;
    }
    getProfileEntity() {
        return this.profileEntity$;
    }
    clearProfileEntity() {
        this.profileEntity = null;
        this.profileEntity$.next(this.profileEntity);
    }

    selectOutletForProfile(outletId: string) {
        if (this.profileEntity) {
            this.profileEntity = null;
            this.profileEntity$.next(this.profileEntity);
        }
        this.fullEntityRequest = this.outletsPublicApi.getOutlet(outletId).subscribe(
            async outlet => {
                this.profileEntity = await this.journodexEntityFactory.createOutlet(outlet);
                this.profileEntity$.next(this.profileEntity);
                this.fullEntityRequest = null;
            }
        );
    }

    // TODO : cache full contacts
    setFocusedResult(result: TargetingResultModel, outletId: string = null) {
        this.targetingRhpContextsService.setEntityByResult(result);

        this.focusedResult = result;
        this.focusedResult$.next(this.focusedResult);
    }

    focusedResultSelectedEmploymentChanged() {
        this.focusedResult$.next(this.focusedResult);
    }

    setSortOrder(order: SortOrderEnum): void {
        if (this.sortOrder !== order) {
            this.sortOrder = order;
            this.sortOrder$.next(order);
            this.refreshResults();
        }
    }

    setSortField(field: any): void {
        if (field !== this.sortField) {
            this.sortField = field;
            this.sortField$.next(field);
            // Check Confluence for reference
            switch (field.enumValue) {
                case SortFieldEnum.CIRCULATION_UNIQUE_VISITORS:
                    this.setSortOrder(SortOrderEnum.descending);
                    break;
                case SortFieldEnum.FIRST_NAME:
                case SortFieldEnum.LAST_NAME:
                case SortFieldEnum.JOB_TITLE:
                case SortFieldEnum.OUTLET_NAME:
                case SortFieldEnum.COUNTRY_NAME:
                case SortFieldEnum.PROVINCE_NAME:
                case SortFieldEnum.CITY_NAME:
                    this.setSortOrder(SortOrderEnum.ascending);
                    break;
            }
            this.refreshResults();
        }
    }

    // Used by the list picker service to pass the current search context verbatim
    // to the backend so that "all selected results" can be added to a list by the backend
    // without requiring the front-end to buffer all the entity ids.
    getCurrentSearchContext(): TargetingQueryApi {

        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }
        if (this.search) {
            return this.search.toApiModel(false, filters).query;
        } else {
            const search = new TargetingSearch();
            const listSearch: TargetingSearchApi = search.toApiModel(false, filters);

            listSearch.query.searchMode = null;
            listSearch.query.rectangleQuery = this.rectangleQuery;
            if (this.list?.id) {
                listSearch.query.list.include = [this.list.id];
            }

            return listSearch.query;
        }
    }

    //#region filters

    setFiltersLocked(locked: boolean): void {
        this.filtersLocked = locked;
        this.filtersLocked$.next(this.filtersLocked);

        this.updateLockedFiltersSessionStorage();
    }

    getFiltersLocked(): Observable<boolean> {
        return this.filtersLocked$;
    }

    filtersAreLocked(): boolean {
        return this.filtersLocked;
    }

    rhpSelectedEmployment():  Observable<boolean> {
        return this.rhpSelectedEmployment$;
    }

    override addListsToFilters(lists: TargetingListList[]) {
        if (lists?.length > 0) {
            super.addListsToFilters(lists);
        }

        // Apply filters
        this.initApplyFilter$.next();
        this.initApplyFilter$.complete();
    }

    setEmploymentForFocusedResult(employment: EmploymentModel): void {
        this.focusedResult.selectedEmployment = employment;
        this.rhpSelectedEmployment$.next(true);
    }

    hasActiveNoticeFilterApplied(): Observable<boolean> {
        return this.filters$.pipe(
            filter(filters => !!filters),
            map(filters => {
                const currentValue = this.statusFilter.getCurrentValue();
                return currentValue.hasActiveNotices;
            })
        );
    }

    applyActiveNoticeFilter(): void {
        const currentValue = this.statusFilter.getCurrentValue();
        if (currentValue.hasActiveNotices) {
            return;
        }
        currentValue.hasActiveNotices = true;
        this.statusFilter.setValue(currentValue);
        this.applyFilters();
    }

    protected setFilters(filters: FilterTypeModel[]): void {
        super.setFilters(filters);

        // TODO : Make sure this is correct
        if (this.filtersLocked) {
            this.updateLockedFiltersSessionStorage();
        }
    }

    private updateLockedFiltersSessionStorage(): void {
        if (this.filtersLocked) {
            const filters = this.generateFiltersSearch();
            const apiQuery = filters.toApiModel();
            sessionStorage.setItem(this.lockedFiltersSessionKey, JSON.stringify(apiQuery));
        } else {
            sessionStorage.removeItem(this.lockedFiltersSessionKey);
        }
    }
    private loadLockedFiltersFromSession(): void {
        const filtersRaw: string = sessionStorage.getItem(this.lockedFiltersSessionKey);
        if (filtersRaw) {
            try {
                const apiModel = JSON.parse(filtersRaw) as TargetingSearchApi;
                let filters = new TargetingSearch(apiModel);
                filters = this.generateStatusFilters(filters);
                if (!this.allowActiveNoticeFilter && filters.hasActiveNotices) {
                    filters.hasActiveNotices = false;
                }
                this.populateFiltersFromSearch(filters);
                this.filtersLocked = true;
                this.filtersLocked$.next(this.filtersLocked);
                this.applyFilters(false);
            } catch (err) {
                this.clearFilters();
            }
        }
    }

    /**
     * Sets the sortOrder, sortField, and pageSize using the current displaySettings
     */
    private setDisplaySettings(displaySettings: UserAppTargetingDisplaySettingsModel) {
        this.sortOrder = displaySettings.sortDirection;
        this.pageCriteria.pageSize = displaySettings.pageSize;
        this.sortField = this.targetingSortFields$.value
            .find(option => option.enumValue === displaySettings.sortType)
            ?? this.sortField;

        this.sortOrder$.next(this.sortOrder);
        this.pageCriteria$.next(this.pageCriteria);
        this.sortField$.next(this.sortField);
    }

    private setSortFieldWithDefault(sortFields: LocalizedEnum[], searchValue: string = this.sortField?.enumValue) {
        this.sortField = sortFields?.find(x => x.enumValue === searchValue)
            ?? sortFields[0];
        this.sortField$.next(this.sortField);
    }
}
